import React, { HTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';

interface Props
  extends Omit<
    HTMLAttributes<HTMLInputElement>,
    'name' | 'className' | 'type'
  > {
  name: string;
  disabled?: boolean;
  checked?: boolean;
  value?: string | number;
  children: ReactNode;
}

const RadioItemUi = ({ name, children, disabled, ...props }: Props) => (
  <RadioWrapper className="way__radio-component__wrapper">
    <LabelWrap className={disabled ? 'label-disabled' : ''}>
      <Input
        name={name}
        disabled={disabled}
        className={disabled ? 'input-disabled' : ''}
        type="radio"
        {...props}
      />
      <Mark />
      <SpanChild
        checked={props.checked ?? false}
        className="way__radio-component__span-child"
      >
        {children}
      </SpanChild>
    </LabelWrap>
  </RadioWrapper>
);

export const RadioButtonItem = ({
  name,
  children,
  disabled,
  checked,
  ...props
}: Props) => (
  <RadioLabelButton
    className={`way__radio-component__wrapper${
      disabled ? ' label-disabled' : ''
    }`}
    selected={checked}
    // Temporary fix. Styled component class name collision is happening for the selected/unselected background colors.
    style={{
      backgroundColor: checked
        ? `var(--way-colors-primaryColorShades-${disabled ? '50' : '100'})`
        : 'transparent',
      borderColor: checked
        ? 'var(--way-colors-primaryColorShades-100)'
        : 'var(--way-colors-borderColor)',
      color: checked
        ? 'var(--way-colors-primaryColorContrastShades-100)'
        : 'var(--way-colors-contrastColorShades-80)',
    }}
  >
    <VisuallyHiddenInput
      name={name}
      disabled={disabled}
      checked={checked}
      type="radio"
      {...props}
    />
    {children}
  </RadioLabelButton>
);

export default function RadioButton({
  asBtn,
  ...props
}: Props & { asBtn?: boolean }) {
  if (asBtn) {
    return <RadioButtonItem {...props} />;
  }

  return <RadioItemUi {...props} />;
}

const RadioWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  & .label-disabled {
    cursor: not-allowed !important;
    opacity: 0.4 !important;
  }
  & .input-disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const RadioLabelButton = styled.label<{ selected?: boolean }>`
  cursor: pointer;
  width: 100%;
  height: 40px;
  min-width: 48px;
  padding: 0 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
  font-size: 15px;
  line-height: 20px;
  font-family: ${({ theme }) => theme.font.secondaryFont};
  border-radius: var(--way-design-borderRadiusDefault);
  transition: all 0.2s;
  border: solid 0.5px
    ${({ selected }) =>
      selected
        ? 'var(--way-colors-primaryColorShades-100)'
        : 'var(--way-colors-borderColor)'};
  background-color: ${({ selected }) =>
    selected ? 'var(--way-colors-primaryColorShades-100)' : 'transparent'};
  color: ${({ selected }) =>
    selected
      ? 'var(--way-colors-primaryColorContrastShades-100)'
      : 'var(--way-colors-contrastColorShades-80)'};

  &:hover {
    background-color: ${({ selected }) =>
      selected ? 'var(--way-colors-primaryColorShades-90)' : 'transparent'};
    color: ${({ selected }) =>
      selected
        ? 'var(--way-colors-primaryColorContrastShades-90)'
        : 'var(--way-colors-contrastColorShades-100)'};
    border: solid 0.5px
      ${({ selected }) =>
        selected ? 'transparent' : 'var(--way-colors-borderHoverColor)'};
  }

  &:focus-within {
    outline: 2px solid var(--way-colors-primaryColorShades-100);
    outline-offset: 1px;
  }

  &.label-disabled {
    cursor: not-allowed;
    opacity: 0.4;
    pointer-events: none;
    background-color: ${({ selected }) =>
      selected ? 'var(--way-colors-primaryColorShades-50)' : 'transparent'};
  }
`;

const VisuallyHiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const Mark = styled.span`
  position: absolute;
  width: 100%;
  height: 100%;
  border: none;
  left: 0;
  top: 0;
  &::after {
    content: '';
    display: block;
    width: 0;
    height: 0;
    background-color: var(--way-colors-primaryColorShades-100);
    opacity: 0;
    left: 50%;
    top: 50%;
    position: absolute;
    transition: all 110ms;
  }
`;

const SpanChild = styled.span<{ checked?: boolean }>`
  position: relative;
  display: block;
  width: 100%;
  color: ${({ checked }) =>
    checked
      ? 'var(--way-colors-primaryColorContrastShades-100)'
      : 'var(--way-colors-secondaryColorContrastShades-100)'};
`;

const Input = styled.input`
  position: absolute;
  visibility: hidden;
  display: none;
  &:checked + ${Mark} {
    + ${SpanChild} {
      color: var(--way-colors-primaryColorContrastShades-100);
    }
    &::after {
      opacity: 1;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
    }
  }
`;

const LabelWrap = styled.label<{ checked?: boolean }>`
  display: flex;
  padding: 6px 6px;
  position: relative;
  font-size: 14px;
  line-height: 20px;
  background-color: ${({ checked }) =>
    checked
      ? 'var(--way-colors-primaryColorShades-100)'
      : 'var(--way-colors-secondaryColorShades-100)'};
  align-items: center;
  width: 100%;
  text-align: center;
  border-radius: 2px;
  font-weight: normal;
  opacity: 1;
  cursor: pointer;
`;
